import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import classNames from 'classnames'
import type {ReactNode} from 'react'
import * as React from 'react'
import {useCallback, useMemo} from 'react'

import type {BuildId, BuildTypeId} from '../../../types'
import {notNull} from '../../../utils/guards'
import {customRunHint} from '../../packages/Hints/HintItems/Hints.buildOverview'
import {useHint} from '../../packages/Hints/Hints.hooks'
import IconButton from '../IconButton/IconButton'
import Popup from '../Popup/Popup.lazy'
import PopupMenu from '../Popup/PopupMenu.lazy'

import StartedBuildMessage from './StartedBuildMessage'

import styles from './RunBuild.css'

type Props = {
  className?: string
  buttonClassName?: string
  splitButtonClassName?: string
  popupClassName?: string
  onClick?: (event: Event | React.SyntheticEvent) => unknown
  onSplitButtonClick?: ((event: Event | React.SyntheticEvent) => unknown) | null | undefined
  loader?: boolean
  disabled: boolean
  children: string
  isPrimary?: boolean
  note?: ReactNode
  icon?: boolean
  noPermission?: boolean
  triggerDisabled?: boolean
  isVisible?: boolean
  buildTypeId: BuildTypeId
  startedBuildId?: BuildId | undefined
  setStartedBuildId?: (buildId?: BuildId) => void
}
export function RunBuild({
  className,
  buttonClassName,
  splitButtonClassName,
  popupClassName,
  onClick,
  onSplitButtonClick,
  loader,
  disabled,
  children,
  isPrimary,
  note,
  icon = false,
  noPermission = false,
  triggerDisabled = false,
  isVisible = false,
  buildTypeId,
  startedBuildId,
  setStartedBuildId,
}: Props) {
  useHint(isVisible && !noPermission && customRunHint)
  const hasDisabledReason = noPermission || triggerDisabled
  const classes = classNames(className, styles.buttonGroup, {
    [styles.invisible]: disabled && !icon,
  })

  const onRunFromMenu = useCallback(
    (_: unknown, event: Event | React.SyntheticEvent) => onClick?.(event),
    [onClick],
  )
  const onRunCustomFromMenu = useCallback(
    (_: unknown, event: Event | React.SyntheticEvent) => onSplitButtonClick?.(event),
    [onSplitButtonClick],
  )
  const dropdown = useMemo(() => {
    const menuItems: (ListDataItem | null)[] = [
      !disabled
        ? {
            label: children,
            onClick: onRunFromMenu,
          }
        : null,
      !disabled && typeof onSplitButtonClick === 'function'
        ? {
            label: 'Run custom build...',
            onClick: onRunCustomFromMenu,
          }
        : null,
    ]

    return (
      <Dropdown
        anchor={
          <IconButton
            className={classes}
            iconWrapperClassName={classNames({
              [styles.disabledIcon]: icon && disabled && hasDisabledReason,
            })}
            icon="run"
            loader={loader}
            disabled={disabled && !hasDisabledReason}
            title={children}
          />
        }
      >
        {hasDisabledReason ? (
          <Popup className={classNames(popupClassName, styles.disabledReasonPopup)}>
            {noPermission
              ? "You don't have enough permissions to\u00a0run this\u00a0build"
              : 'Manual triggering is\u00a0disabled in\u00a0this\u00a0build\u00a0configuration'}
          </Popup>
        ) : (
          <PopupMenu
            className={popupClassName}
            activateFirstItem
            closeOnSelect
            data={menuItems.filter(notNull)}
            hint={note}
          />
        )}
      </Dropdown>
    )
  }, [
    children,
    classes,
    disabled,
    hasDisabledReason,
    icon,
    loader,
    noPermission,
    note,
    onRunCustomFromMenu,
    onRunFromMenu,
    onSplitButtonClick,
    popupClassName,
  ])

  return (
    <>
      {icon ? (
        dropdown
      ) : (
        <>
          {!disabled && note}
          <ButtonGroup className={classes} split>
            <Button
              className={buttonClassName}
              onClick={onClick}
              loader={loader}
              disabled={disabled}
              data-test="run-build"
              title={children}
              primary={isPrimary}
            >
              {children}
            </Button>
            {typeof onSplitButtonClick === 'function' && (
              <Button
                className={splitButtonClassName}
                onClick={onSplitButtonClick}
                short
                loader={loader}
                disabled={disabled}
                primary={isPrimary}
              >
                <span data-hint-container-id={customRunHint.id}>{'...'}</span>
              </Button>
            )}
          </ButtonGroup>
        </>
      )}
      {(loader || startedBuildId != null) && (
        <StartedBuildMessage
          buildTypeId={buildTypeId}
          startedBuildId={startedBuildId}
          setStartedBuildId={setStartedBuildId}
        />
      )}
    </>
  )
}
export default RunBuild
